import { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { getLinkAriaLabel } from '@/utils/urlUtils';

import { MediaWrapper } from '@/components/Cms/MediaWrapper';
import { Flex } from '@/components/Flex';
import { Icon } from '@/components/Icon';
import { SectionHeader } from '@/components/SectionHeader';
import type {
  ContentfulBannerHeroFragment,
  ContentfulImageWithFocalPointFragment,
} from '@/graphql/contentful/api';
import { useIsMdUp } from '@/hooks/useMedia';
import { generateDataTestId } from '@/lib/tests';
import { commonMessages } from '@/messages/common';
import { useRegionProvider } from '@/providers/RegionProvider';

import * as S from './BannerHero.styles';

export interface FocalPoint {
  x: number;
  y: number;
}

export interface ImageWithFocalPointProps {
  focalPoint: {
    focalPoint: FocalPoint;
  };
  image: {
    contentType: string;
    height: number;
    title: string;
    url: string;
    width: number;
  };
}

export interface BannerHeroProps {
  banner: ContentfulBannerHeroFragment;
  type: string | null | undefined;
}

export const BannerHero: React.FC<BannerHeroProps> = ({ banner, type }) => {
  const { locale } = useRegionProvider();
  const headerSeoType = (banner?.titleSeoTag ??
    'h2') as keyof HTMLElementTagNameMap;
  const [isExpanded, setIsExpanded] = useState(false);
  const [needsExpansion, setNeedsExpansion] = useState(false);
  const textRef = useRef<HTMLDivElement>(null);
  const isMdUp = useIsMdUp();

  useEffect(() => {
    const checkTextOverflow = () => {
      if (textRef.current && banner.text) {
        const isOverflowing =
          textRef.current.scrollHeight > textRef.current.clientHeight;
        setNeedsExpansion(isOverflowing);
      }
    };

    const timer = setTimeout(checkTextOverflow, 100);

    window.addEventListener('resize', checkTextOverflow);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', checkTextOverflow);
    };
  }, [banner.text]);

  const BannerMediaWrapper = () => {
    return (
      <MediaWrapper
        {...(banner?.url && {
          'aria-label': getLinkAriaLabel(banner.url, locale.code),
        })}
        {...(banner.image as ContentfulImageWithFocalPointFragment)}
        className={
          type === 'one_hero_product'
            ? 'banner-hero-image-single'
            : 'banner-hero-image-double'
        }
        data-test-id='BaseCardTypeBackgroundImage'
        loaderProps={{ height: 532 }}
        mediaWrapperType='BannerImage'
        preload={false}
        sizes='(max-width: 600px) 50vw, 50vw'
        useLoader
        {...generateDataTestId('productCard', 'image')}
      />
    );
  };

  return (
    <S.Banner data-test-id='BannerHeroBanner'>
      <S.Image as='div' data-test-id='BannerHeroImage'>
        <BannerMediaWrapper />
      </S.Image>
      <S.BannerTextBox
        data-test-id='BannerHeroBannerTextBox'
        position='relative'
        px={5}
        py={5}
      >
        <Flex flexDirection='column'>
          <SectionHeader
            colorized={false}
            data-test-id='BannerHeroSectionHeader'
            htmlElementTag={headerSeoType}
            text={banner.title || ''}
          />
          {banner.text && (
            <Flex flexDirection='column'>
              <S.Text
                ref={textRef}
                $isExpanded={isExpanded}
                dangerouslySetInnerHTML={{
                  __html: banner.text,
                }}
                data-test-id='BannerHeroText'
              />
              <S.SeeMoreButton
                $display={needsExpansion && isMdUp}
                onClick={() => setIsExpanded(!isExpanded)}
              >
                {isExpanded ? (
                  <FormattedMessage
                    {...commonMessages.showLess}
                  ></FormattedMessage>
                ) : (
                  <FormattedMessage
                    {...commonMessages.showMore}
                  ></FormattedMessage>
                )}
              </S.SeeMoreButton>
            </Flex>
          )}
        </Flex>

        {banner.urlType === 'link' && banner.url && (
          <S.StyledLink
            aria-label={
              getLinkAriaLabel(banner.url, locale.code) ||
              `${banner.urlText || 'Discover'} - ${
                banner.title || 'Learn more'
              }`
            }
            data-test-id='BannerHeroStyledLink'
            label={banner.urlText}
            linkProps={{ href: banner.url }}
            variant='link'
          >
            <Icon name='chevronRight' />
          </S.StyledLink>
        )}
        {banner.urlType === 'button' && banner.url && (
          <S.StyledLink
            aria-label={
              getLinkAriaLabel(banner.url, locale.code) ||
              `${banner.urlText || 'Discover'} - ${
                banner.title || 'Learn more'
              }`
            }
            data-test-id='BannerHeroStyledLink'
            href={banner.url}
          >
            {banner.urlText}
          </S.StyledLink>
        )}
      </S.BannerTextBox>
    </S.Banner>
  );
};
